import React, { useState, useRef } from 'react';
import { FilterToolbar } from './FilterToolbar';
import { FilterPanel } from './FilterPanel';
import { useFilters } from '../hooks/useFilters';
import { FilterSchema } from '../types';

/**
 * Active filter display data
 */
interface ActiveFilter {
  key: string;
  label: string;
  value?: string;
}

/**
 * FilterSystem Props
 */
interface FilterSystemProps {
  /** Filter schema defining available filters */
  schema: FilterSchema;
  /** Filter controls to render inside the panel (inputs/selects/ranges) */
  children: React.ReactNode;
  /** Optional title for the panel header */
  title?: string;
  /** Disable interactions (e.g., while loading) */
  disabled?: boolean;
}

/**
 * FilterSystem Component
 * Main orchestrator that ties together FilterToolbar, FilterPanel, and useFilters hook
 *
 * Manages:
 * - Panel open/close state
 * - Active filter display
 * - Filter application and reset
 *
 * State management:
 * - useFilters hook manages URL sync and filter state
 * - FilterSystem only manages UI state (panel open/close)
 */
export function FilterSystem({
  schema,
  children,
  title = 'Filters',
  disabled = false,
}: FilterSystemProps) {
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setOpen] = useState(false);

  // Hook manages filter state and URL synchronization
  const { filters, setFilter, clearFilter, removeFilter, clearAll, activeFilters, activeCount } = useFilters(schema);

  const onOpenPanel = () => setOpen(true);
  const onClose = () => setOpen(false);

  const onRemove = (key: string) => {
    removeFilter(key);
  };

  const onApply = () => {
    // Read input values from *inside the open panel only*
    const panelEl = document.querySelector<HTMLElement>('.c-filter-panel');
    if (!panelEl) return;

    const params = new URLSearchParams(window.location.search);

    // First, remove all known filter params to avoid stale values
    schema.facets.forEach(facet => {
      const paramKey = facet.param ?? facet.id;
      if (paramKey) {
        params.delete(paramKey);
      }
    });

    // Then read current values from inputs by name attribute (scoped to panel)
    schema.facets.forEach(facet => {
      const paramKey = facet.param ?? facet.id;
      if (!paramKey) {
        return;
      }

      const input = panelEl.querySelector<HTMLInputElement | HTMLSelectElement>(
        `[name="${paramKey}"]`
      );

      if (input) {
        const value = input.value?.trim();
        if (value && value !== '') {
          params.set(paramKey, value);
        }
      }
    });

    // Navigate with standard GET (keeps server-side behavior intact)
    const qs = params.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.location.assign(next);
  };

  const onClearAll = () => {
    // Clear hook state
    clearAll();

    // Navigate to base URL without any filter params
    const params = new URLSearchParams(window.location.search);
    schema.facets.forEach(facet => {
      const paramKey = facet.param ?? facet.id;
      if (paramKey) {
        params.delete(paramKey);
      }
    });

    const qs = params.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.location.assign(next);
  };

  return (
    <>
      {/* Toolbar sits inline with page content */}
      <div className="c-filter-form">
        <FilterToolbar
          filters={activeFilters}
          onRemove={onRemove}
          onClearAll={onClearAll}
          onOpenPanel={onOpenPanel}
          disabled={disabled}
        />
      </div>

      {/* Fly-out panel is portaled; focus/scroll managed inside the component */}
      <FilterPanel
        isOpen={isOpen}
        title={title}
        onClose={onClose}
        onApply={onApply}
        onReset={onClearAll}
        initialFocusRef={openerRef}
      >
        {children}
      </FilterPanel>
    </>
  );
}
